import { Check, Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Environment } from '~/engine/data/Environment';
import {
    ContourAutoSuggestionController,
    type PendingContourSuggestion,
} from '~/engine/tool/contourAutoSuggestion/ContourAutoSuggestionController';
import {
    type CanvasLoop,
    loopReachesRect,
    loopsShowOnCanvas,
} from '~/engine/tool/contourAutoSuggestion/ContourAutoSuggestionPreviewRenderer';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { usePendingContourSuggestion } from '~/hooks/useContourAutoSuggestionController';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { useChangeVersion } from '~/viewportGrid/adapters/useChangeVersion';
import {
    POINTER_SHIELD_ATTRIBUTE,
    READOUT_SURFACE_SX,
} from '~/viewportGrid/chrome';
import { usePlaneViewHostedBy } from '../usePlaneViewHostedBy';
import type { CanvasRect } from './VoiSelector/projectVoiBounds';

const BUTTON_SIZE_PX = 24;
const BUTTON_GAP_PX = 2;
const BOX_EDGE_PX = 2;
const BOX_SIZE = {
    width: 2 * BUTTON_SIZE_PX + BUTTON_GAP_PX + 2 * BOX_EDGE_PX,
    height: BUTTON_SIZE_PX + 2 * BOX_EDGE_PX,
};

/** The emptied box stays this long after a click, to take the rest of a double-click. */
const ACTIVATION_GUARD_MS = 500;

export const OUTLINE_GAP_PX = 4;

/** Keeps the box off a tile's edge letters and corner readouts. */
export const TILE_INSET_PX = 24;

export type OutlineLoop = CanvasLoop;

type Size = Pick<CanvasRect, 'width' | 'height'>;

/** A box's top-left corner, in CSS px from the tile's top-left corner. */
export type BoxSpot = Pick<CanvasRect, 'left' | 'top'>;

const BOX_SX = {
    position: 'absolute',
    zIndex: 9, // Above every other layer over a tile's canvas, which top out at 8.
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: `${BUTTON_GAP_PX}px`,
    ...BOX_SIZE,
    pointerEvents: 'auto',
} as const;

const buttonSx = (highlight: string) => ({
    width: BUTTON_SIZE_PX,
    height: BUTTON_SIZE_PX,
    padding: 0,
    fontSize: 18,
    color: 'common.white',
    '&:hover': { color: highlight },
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: highlight },
});
const ACCEPT_BUTTON_SX = buttonSx('primary.main');
const DECLINE_BUTTON_SX = buttonSx('error.main');

const acceptSuggestion = () =>
    ContourAutoSuggestionController.getInstance().accept();
const declineSuggestion = () =>
    ContourAutoSuggestionController.getInstance().decline();

function boundingRectOf(loops: OutlineLoop[]): CanvasRect | undefined {
    const min = [Infinity, Infinity];
    const max = [-Infinity, -Infinity];
    for (const point of loops.flat()) {
        for (const axis of [0, 1]) {
            min[axis] = Math.min(min[axis], point[axis]);
            max[axis] = Math.max(max[axis], point[axis]);
        }
    }
    if (![...min, ...max].every(Number.isFinite)) {
        return undefined;
    }
    const [left, top] = min.map(Math.floor);
    const [right, bottom] = max.map(Math.ceil);
    return { left, top, width: right - left, height: bottom - top };
}

/**
 * The spot for a box beside `outline` on a tile, in CSS px from the tile's
 * top-left corner: next to the outline's bounding rect, else a free spot in
 * the tile. Undefined when the tile shows none of the outline or has no room.
 */
export function placeBesideOutline(
    outline: ReadonlyArray<OutlineLoop>,
    tile: Size,
    box: Size
): BoxSpot | undefined {
    const loops = outline.filter((loop) => loop.length >= 2);
    const bounds = boundingRectOf(loops);
    const maxLeft = tile.width - TILE_INSET_PX - box.width;
    const maxTop = tile.height - TILE_INSET_PX - box.height;
    if (!bounds || maxLeft < TILE_INSET_PX || maxTop < TILE_INSET_PX) {
        return undefined;
    }
    if (!loopsShowOnCanvas(loops, tile.width, tile.height)) {
        return undefined;
    }
    const slidIntoTile = ({ left, top }: BoxSpot): BoxSpot => ({
        left: Math.min(Math.max(left, TILE_INSET_PX), maxLeft),
        top: Math.min(Math.max(top, TILE_INSET_PX), maxTop),
    });
    const right = bounds.left + bounds.width;
    const bottom = bounds.top + bounds.height;
    const clearOfBounds = ({ left, top }: BoxSpot) =>
        left >= right + OUTLINE_GAP_PX ||
        left + box.width <= bounds.left - OUTLINE_GAP_PX ||
        top >= bottom + OUTLINE_GAP_PX ||
        top + box.height <= bounds.top - OUTLINE_GAP_PX;
    const flushRight = right - box.width;
    const besideBounds: BoxSpot[] = [
        { left: right + OUTLINE_GAP_PX, top: bounds.top },
        { left: bounds.left - OUTLINE_GAP_PX - box.width, top: bounds.top },
        { left: flushRight, top: bounds.top - OUTLINE_GAP_PX - box.height },
        { left: flushRight, top: bottom + OUTLINE_GAP_PX },
    ];
    const middleLeft = Math.round((TILE_INSET_PX + maxLeft) / 2);
    const middleTop = Math.round((TILE_INSET_PX + maxTop) / 2);
    const insideTile: BoxSpot[] = [
        { left: maxLeft, top: TILE_INSET_PX },
        { left: TILE_INSET_PX, top: TILE_INSET_PX },
        { left: maxLeft, top: maxTop },
        { left: TILE_INSET_PX, top: maxTop },
        { left: middleLeft, top: TILE_INSET_PX },
        { left: middleLeft, top: maxTop },
        { left: TILE_INSET_PX, top: middleTop },
        { left: maxLeft, top: middleTop },
        { left: middleLeft, top: middleTop },
    ];
    return (
        besideBounds.map(slidIntoTile).find(clearOfBounds) ??
        insideTile.find((spot) => {
            const reach = {
                left: spot.left - OUTLINE_GAP_PX,
                top: spot.top - OUTLINE_GAP_PX,
                width: box.width + 2 * OUTLINE_GAP_PX,
                height: box.height + 2 * OUTLINE_GAP_PX,
            };
            return !loops.some((loop) => loopReachesRect(loop, reach));
        })
    );
}

function placeOn(view: PlaneView, suggestion: PendingContourSuggestion) {
    const outline = suggestion.outlineMm.map((loop) =>
        loop.map((pointMm) => view.canvasCoordsFromPatientCoords(pointMm))
    );
    const [width, height] = view.size;
    return placeBesideOutline(outline, { width, height }, BOX_SIZE);
}

interface ContourSuggestionActionsProps {
    renderer: RenderAdapter;
    /** The Environment whose tiles show the buttons; undefined hides them. */
    suggestionHost: Environment | undefined;
}

/**
 * The accept and decline buttons of the contour suggestion, in a box beside
 * its outline on one grid tile of the Environment that hosts them. Renders
 * nothing while the tile shows none of it. Re-renders on every cursor move
 * and display-scale change, so the buttons follow the ghost.
 */
export default function ContourSuggestionActions({
    renderer,
    suggestionHost,
}: ContourSuggestionActionsProps) {
    useChangeVersion(renderer.getCursorSource());
    useChangeVersion(renderer.getDisplayScaleSource());
    const view = usePlaneViewHostedBy(renderer, suggestionHost);
    const pending = usePendingContourSuggestion();
    const { formatMessage } = useIntl();
    const acceptLabel = formatMessage({ id: 'sliceDeformSuggestAccept' });
    const declineLabel = formatMessage({ id: 'sliceDeformSuggestDecline' });
    const [box, setBox] = useState<HTMLDivElement | null>(null);
    const [guardedSpot, setGuardedSpot] = useState<BoxSpot>();
    // The guard goes with the box on a tile that stops hosting the buttons.
    if (!view && guardedSpot) {
        setGuardedSpot(undefined);
    }

    // Native, on the box: the engine listens on an ancestor of the tile and
    // routes a release and a double-click by pointer position, so they are
    // stopped here before they reach it.
    useLayoutEffect(() => {
        if (!box) {
            return undefined;
        }
        // Set at the document in the capture phase: every press resets it,
        // whatever stops the press further down.
        let pressBeganOnBox = false;
        const notePressOrigin = ({ target }: MouseEvent) => {
            pressBeganOnBox = target instanceof Node && box.contains(target);
        };
        const keepPress = (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
        };
        const keepOwnRelease = (event: MouseEvent) => {
            if (pressBeganOnBox) {
                event.stopPropagation();
            }
        };
        const keepDoubleClick = (event: MouseEvent) => event.stopPropagation();
        document.addEventListener('mousedown', notePressOrigin, true);
        box.addEventListener('mousedown', keepPress);
        box.addEventListener('mouseup', keepOwnRelease);
        box.addEventListener('dblclick', keepDoubleClick);
        return () => {
            document.removeEventListener('mousedown', notePressOrigin, true);
            box.removeEventListener('mousedown', keepPress);
            box.removeEventListener('mouseup', keepOwnRelease);
            box.removeEventListener('dblclick', keepDoubleClick);
        };
    }, [box]);

    useEffect(() => {
        if (!guardedSpot) {
            return undefined;
        }
        const endGuard = () => setGuardedSpot(undefined);
        const timer = window.setTimeout(endGuard, ACTIVATION_GUARD_MS);
        return () => window.clearTimeout(timer);
    }, [guardedSpot]);

    const placement =
        view && pending?.views.includes(view) && !guardedSpot
            ? placeOn(view, pending)
            : undefined;
    // Read during render: once the buttons are gone, the focus has already
    // fallen to the page.
    const takesFocusedButtonAway =
        !placement && box !== null && box.contains(document.activeElement);
    useLayoutEffect(() => {
        if (takesFocusedButtonAway) {
            view?.canvas
                ?.closest<HTMLElement>('[data-tile-id]')
                ?.focus({ preventScroll: true });
        }
    }, [takesFocusedButtonAway, view]);

    const spot = guardedSpot ?? placement;
    if (!view || !spot) {
        return null;
    }
    const activate = (handler: () => void) => {
        setGuardedSpot(spot);
        handler();
    };
    return (
        <Box
            ref={setBox}
            data-testid="contour-suggestion-actions"
            {...{ [POINTER_SHIELD_ATTRIBUTE]: '' }}
            // Inline: `sx` would mint a class for every pan and zoom.
            style={{ left: spot.left, top: spot.top }}
            sx={[BOX_SX, placement !== undefined && READOUT_SURFACE_SX]}
        >
            {placement && (
                <>
                    <IconButton
                        size="small"
                        aria-label={acceptLabel}
                        data-testid="contour-suggestion-accept"
                        onClick={() => activate(acceptSuggestion)}
                        sx={ACCEPT_BUTTON_SX}
                    >
                        <Check fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={declineLabel}
                        aria-keyshortcuts="Escape"
                        data-testid="contour-suggestion-decline"
                        onClick={() => activate(declineSuggestion)}
                        sx={DECLINE_BUTTON_SX}
                    >
                        <Close fontSize="inherit" />
                    </IconButton>
                </>
            )}
        </Box>
    );
}
